// apiService.js - Enhanced Frontend API Integration with Better Error Handling
import logger from './utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class APIService {
  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    // Simple performance monitoring without circular dependency
    const startTime = performance.now();
    try {
      const response = await fetch(url, config);

        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }

        if (!response.ok) {
          const error = await response.json().catch(() => ({
            error: `Request failed with status ${response.status}`
          }));

          // Log detailed error for debugging
          logger.error('API Error Details:', {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            error: error.error || error.message || error
          });

          throw new Error(error.error || error.message || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        const duration = performance.now() - startTime;
        
        // Log slow API calls
        if (duration > 1000) {
          logger.performance(`API ${endpoint}`, duration);
        }
        
        return data;
      } catch (error) {
        const duration = performance.now() - startTime;
        logger.error('API Request Error:', {
          endpoint,
          message: error.message,
          duration: duration.toFixed(2) + 'ms',
          stack: error.stack
        });
        throw error;
      }
  }

  // Authentication
  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    this.token = response.token;
    localStorage.setItem('authToken', response.token);
    localStorage.setItem('userData', JSON.stringify({
      ldapUser: response.ldapUser,
      sipNumber: response.sipNumber,
      sipPassword: response.sipPassword,
      allowedQueues: response.allowedQueues
    }));

    return response;
  }

  logout() {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  }

  getUserData() {
    const data = localStorage.getItem('userData');
    return data ? JSON.parse(data) : null;
  }

  isAuthenticated() {
    return !!this.token;
  }

  // Queue Management
  async getQueueStats() {
    return await this.request('/queues/stats');
  }

  async loginToQueue(queueName) {
    return await this.request('/queue/login', {
      method: 'POST',
      body: JSON.stringify({ queueName })
    });
  }

  async logoutFromQueue(queueName) {
    return await this.request('/queue/logout', {
      method: 'POST',
      body: JSON.stringify({ queueName })
    });
  }

  async setQueuePause(queueName, paused) {
    return await this.request('/queue/pause', {
      method: 'POST',
      body: JSON.stringify({ queueName, paused })
    });
  }

  async getMyQueueMemberships() {
    return await this.request('/queue/my-memberships');
  }

  async removeAgentFromQueue(queueName, agentSip) {
    return await this.request('/queue/remove-agent', {
      method: 'POST',
      body: JSON.stringify({ queueName, agentSip })
    });
  }

  // CDR Management
  async getCDRRecords(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });

    const queryString = params.toString();
    const endpoint = `/cdr${queryString ? `?${queryString}` : ''}`;

    return await this.request(endpoint);
  }

  async getCDRDetail(id) {
    return await this.request(`/cdr/${id}`);
  }

  async updateCDR(id, data) {
    return await this.request(`/cdr/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async updateCallHoldDuration(id, holdData) {
    return await this.request(`/cdr/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(holdData)
    });
  }

  async addNote(id, noteText) {
    return await this.request(`/cdr/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ noteText })
    });
  }

  // Tags
  async getTags() {
    return await this.request('/tags');
  }

  async createTag(tagData) {
    return await this.request('/tags', {
      method: 'POST',
      body: JSON.stringify(tagData)
    });
  }

  // CSV Import
  async importCSVPreview(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/cdr/import/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Preview failed' }));
      throw new Error(error.error || 'Preview failed');
    }

    return await response.json();
  }

  async importCSVExecute(file, mapping) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));

    const response = await fetch(`${API_BASE_URL}/cdr/import/execute`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(error.error || 'Import failed');
    }

    return await response.json();
  }

  // Statistics
  async getAgentStatistics(sipNumber, startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();
      const endpoint = `/statistics/agent/${sipNumber}${queryString ? `?${queryString}` : ''}`;

      return await this.request(endpoint);
    } catch (error) {
      console.error('Failed to load agent statistics:', error);
      // Return empty array instead of throwing to prevent UI from breaking
      return [];
    }
  }

  async getOverallStatistics(startDate = null, endDate = null, queueName = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (queueName) params.append('queueName', queueName);

      const queryString = params.toString();
      const endpoint = `/statistics/overall${queryString ? `?${queryString}` : ''}`;

      return await this.request(endpoint);
    } catch (error) {
      console.error('Failed to load overall statistics:', error);
      return null;
    }
  }

  async getHourlyStatistics(startDate = null, endDate = null, queueName = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (queueName) params.append('queueName', queueName);

      const queryString = params.toString();
      const endpoint = `/statistics/hourly${queryString ? `?${queryString}` : ''}`;

      return await this.request(endpoint);
    } catch (error) {
      console.error('Failed to load hourly statistics:', error);
      return [];
    }
  }

  async getDailyStatistics(startDate = null, endDate = null, queueName = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (queueName) params.append('queueName', queueName);

      const queryString = params.toString();
      const endpoint = `/statistics/daily${queryString ? `?${queryString}` : ''}`;

      return await this.request(endpoint);
    } catch (error) {
      console.error('Failed to load daily statistics:', error);
      return [];
    }
  }

  async refreshStatistics() {
    return await this.request('/statistics/refresh', {
      method: 'POST'
    });
  }

  // Call Transfer
  async getActiveChannel() {
    return await this.request('/call/active-channel');
  }

  async transferToQueue(queueName) {
    return await this.request('/call/transfer-to-queue', {
      method: 'POST',
      body: JSON.stringify({ queueName })
    });
  }

  async transferToExtension(extension) {
    return await this.request('/call/transfer-to-extension', {
      method: 'POST',
      body: JSON.stringify({ extension })
    });
  }

  // Recordings
  //getRecordingUrl(recordingId) {
    //return `${API_BASE_URL}/recordings/${recordingId}?token=${this.token}`;
  //}
// Contacts Management
  async lookupContact(phoneNumber) {
    try {
      return await this.request(`/contacts/lookup/${phoneNumber}`);
    } catch (error) {
      console.error('Contact lookup failed:', error);
      return null;
    }
  }

  async searchContacts(phoneNumber) {
    try {
      return await this.request(`/contacts/search/${phoneNumber}`);
    } catch (error) {
      console.error('Contact search failed:', error);
      return [];
    }
  }

  async getContacts(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });

    const queryString = params.toString();
    const endpoint = `/contacts${queryString ? `?${queryString}` : ''}`;

    return await this.request(endpoint);
  }

  async createContact(contactData) {
    return await this.request('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  async updateContact(phoneNumber, contactData) {
    return await this.request(`/contacts/${phoneNumber}`, {
      method: 'PATCH',
      body: JSON.stringify(contactData)
    });
  }

  async deleteContact(phoneNumber) {
    return await this.request(`/contacts/${phoneNumber}`, {
      method: 'DELETE'
    });
  }

  async importLDAPContacts(config = {}) {
    return await this.request('/contacts/import-ldap', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  async getLDAPFieldMappings() {
    return await this.request('/contacts/ldap/field-mappings');
  }

  async getLDAPDepartmentFilters() {
    return await this.request('/contacts/ldap/department-filters');
  }

  async getLDAPImportHistory(params = {}) {
    const queryParams = new URLSearchParams(params);
    return await this.request(`/contacts/ldap/import-history${queryParams.toString() ? `?${queryParams}` : ''}`);
  }

  async rollbackLDAPImport(importId) {
    return await this.request(`/contacts/ldap/rollback/${importId}`, {
      method: 'POST'
    });
  }

  // Reports
    async getAgentKPIReport(startDate = null, endDate = null, queueName = null) {
      try {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (queueName) params.append('queueName', queueName);

        const queryString = params.toString();
        const endpoint = `/reports/agent-kpi${queryString ? `?${queryString}` : ''}`;

        return await this.request(endpoint);
      } catch (error) {
        console.error('Failed to load KPI report:', error);
        return [];
      }
    }

//    getKPIReportExportURL(startDate = null, endDate = null, queueName = null) {
//      const params = new URLSearchParams();
//      if (startDate) params.append('startDate', startDate);
//      if (endDate) params.append('endDate', endDate);
//      if (queueName) params.append('queueName', queueName);
//      params.append('token', this.token);
//
//      return `${API_BASE_URL}/reports/agent-kpi/export?${params.toString()}`;
//    }

  // Optional: Add this method to check available auth methods
  async getAuthMethods() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/methods`);
      return await response.json();
    } catch (error) {
      console.error('Failed to get auth methods:', error);
      return { ldap: { available: false }, entraId: { available: false } };
    }
  }

// User Management
async getUsers() {
  return await this.request('/admin/users');
}

async createUser(userData) {
  return await this.request('/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData)
  });
}

async getAvailableQueues() {
  return await this.request('/admin/queues/available');
}

async getAllQueues() {
  return await this.request('/queues/all');
}

async getAgentsStatus() {
  return await this.request('/agents/status');
}

async getAvailableSipNumbers() {
  return await this.request('/admin/sip-numbers/available');
}

async updateUser(username, userData) {
  return await this.request(`/admin/users/${username}`, {
    method: 'PATCH',
    body: JSON.stringify(userData)
  });
}

async deleteUser(username) {
  return await this.request(`/admin/users/${username}`, {
    method: 'DELETE'
  });
}

// Recordings
getRecordingUrl(callId) {
  return `${API_BASE_URL}/recordings/url/${callId}?token=${this.token}`;
}

getRecordingStreamUrl(filename) {
  // Extract just the filename from the path if needed
  const actualFilename = filename.split('/').pop();
  return `${API_BASE_URL}/recordings/${actualFilename}?token=${this.token}`;
}

getKPIReportExportURL(startDate = null, endDate = null, queueName = null) {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (queueName) params.append('queueName', queueName);
  params.append('token', this.token);

  return `${API_BASE_URL}/reports/agent-kpi/export?${params.toString()}`;
}

async getRecordingInfo(callId) {
  return await this.request(`/recordings/url/${callId}`);
}

  // Queue Configuration Management
  async getQueueConfigs() {
    return await this.request('/queues/config');
  }

  async getQueueConfig(queueName) {
    return await this.request(`/queues/config/${queueName}`);
  }

  async updateQueueConfig(queueName, config) {
    return await this.request(`/queues/config/${queueName}`, {
      method: 'PUT',
      body: JSON.stringify(config)
    });
  }

  async uploadQueueAudio(queueName, fileType, file) {
    const formData = new FormData();
    formData.append('audioFile', file);
    formData.append('queueName', queueName);
    formData.append('fileType', fileType);
    
    const response = await fetch(`${API_BASE_URL}/queues/audio/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    
    return await response.json();
  }

  async deleteQueueAudio(audioId) {
    return await this.request(`/queues/audio/${audioId}`, {
      method: 'DELETE'
    });
  }

  async generateQueueConfig() {
    return await this.request('/queues/generate-config', {
      method: 'POST'
    });
  }

  async syncQueuesFromAsterisk() {
    return await this.request('/queues/sync-from-asterisk', {
      method: 'POST'
    });
  }

  // Health Check
  async healthCheck() {
    try {
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'error', error: error.message };
    }
  }

  // Missed Call Notifications
  async getMissedCalls(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    return await this.request(`/agent/missed-calls${queryString ? `?${queryString}` : ''}`);
  }

  async getMissedCallStats(days = 7) {
    return await this.request(`/agent/missed-calls/stats?days=${days}`);
  }

  async getMissedCallsByQueue(days = 7) {
    return await this.request(`/agent/missed-calls/by-queue?days=${days}`);
  }

  async getNotificationPreferences() {
    return await this.request('/agent/notification-preferences');
  }

  async updateNotificationPreferences(preferences) {
    return await this.request('/agent/notification-preferences', {
      method: 'POST',
      body: JSON.stringify(preferences)
    });
  }

  async markMissedCallRead(id) {
    return await this.request(`/agent/missed-calls/${id}/read`, {
      method: 'POST'
    });
  }

  async markAllMissedCallsRead() {
    return await this.request('/agent/missed-calls/read-all', {
      method: 'POST'
    });
  }

  async callbackMissedCall(id) {
    return await this.request(`/agent/missed-calls/${id}/callback`, {
      method: 'POST'
    });
  }

  // Category APIs
  async getCategoriesLvl1() {
    return await this.request('/categories/lvl1');
  }

  async getCategoriesLvl2(lvl1Id) {
    return await this.request(`/categories/lvl2/${lvl1Id}`);
  }

  async getCategoriesLvl3(lvl2Id) {
    return await this.request(`/categories/lvl3/${lvl2Id}`);
  }

  async getCallTypes() {
    return await this.request('/call-types');
  }

  async getCategories() {
    return await this.request('/categories');
  }

  async createCategory(categoryData) {
    return await this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async updateCategory(id, categoryData) {
    return await this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  }

  async deleteCategory(id) {
    return await this.request(`/categories/${id}`, {
      method: 'DELETE'
    });
  }

  async exportCategories() {
    const response = await fetch(`${API_BASE_URL}/categories/export`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to export categories');
    return response.blob();
  }

  async importCategories(xmlData) {
    return await this.request('/categories/import', {
      method: 'POST',
      body: JSON.stringify({ xmlData })
    });
  }

  async previewImport(xmlData) {
    return await this.request('/categories/import/preview', {
      method: 'POST',
      body: JSON.stringify({ xmlData })
    });
  }

  async getAuditLog(filters = {}) {
    const params = new URLSearchParams(filters);
    return await this.request(`/categories/audit?${params}`);
  }
}

// Create singleton instance
const apiService = new APIService();

export default apiService;